import { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { EquipmentExtendedClient } from '@frontend/equipment/api';
import { EquipmentStateName } from '@frontend/equipment/types';
import type { EquipmentExtended } from '@frontend/equipment/types';
import { Logger } from '@frontend/logger';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { useCallback, useEffect, useState } from 'react';

import type { EquipmentSelectionListProps } from './equipment-selection-list.component';

export type EquipmentStateFilter = EquipmentStateName;

interface ViewProps {
    selectedIds: Set<string>;
    selectedCount: number;
    visibleStockCount: number;
    activeFilter: EquipmentStateFilter;
    setActiveFilter: (filter: EquipmentStateFilter) => void;
    equipmentById: Map<string, EquipmentExtended>;
    allSelected: boolean;
    fetch: (args?: ApiQueryParams<DefaultQueryParams>) => ReturnType<typeof StockExtendedClient.fetchReusableStocksExtended>;
    onDataLoaded: (_items: StockExtended[]) => void;
    toggleSelection: (_id: string) => void;
    toggleAll: () => void;
    onBack: () => void;
    onContinue: () => void;
    isSubmitting: boolean;
}

const useEquipmentSelectionList = (props: EquipmentSelectionListProps): ViewProps => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectedStocks, setSelectedStocks] = useState<Map<string, StockExtended>>(new Map());
    const [loadedStock, setLoadedStock] = useState<StockExtended[]>([]);
    const [equipmentById, setEquipmentById] = useState<Map<string, EquipmentExtended>>(new Map());
    const [activeFilter, setActiveFilter] = useState<EquipmentStateFilter>(EquipmentStateName.EQUIPMENT_ANY_STATE);

    useEffect(() => {
        setLoadedStock([]);
    }, [activeFilter]);

    const fetch = useCallback(
        (args?: ApiQueryParams<DefaultQueryParams>) =>
            StockExtendedClient.fetchReusableStocksExtended({
                ...args,
                ...(activeFilter === EquipmentStateName.EQUIPMENT_ANY_STATE ? {} : { equipment_state: activeFilter })
            }),
        [activeFilter]
    );

    const fetchEquipmentDetails = useCallback(
        async (items: StockExtended[]) => {
            const equipmentIds = [...new Set(items.map((item) => item.equipment_id).filter((id): id is string => !!id))];
            const missingEquipmentIds = equipmentIds.filter((id) => !equipmentById.has(id));

            if (missingEquipmentIds.length === 0) return;

            try {
                const fetchedEquipments = await Promise.all(
                    missingEquipmentIds.map((id) => {
                        const stock = items.find((item) => item.equipment_id === id);
                        return EquipmentExtendedClient.fetchEquipmentExtended(stock?.account_id ?? '', id);
                    })
                );

                setEquipmentById((prev) => {
                    const next = new Map(prev);
                    fetchedEquipments.forEach((equipment) => next.set(equipment.id, equipment));
                    return next;
                });
            } catch (error) {
                Logger.error('Failed to fetch equipment details for lending equipment cards', error);
            }
        },
        [equipmentById]
    );

    const onDataLoaded = useCallback(
        (items: StockExtended[]) => {
            setLoadedStock((prev) => {
                const existingIds = new Set(prev.map((s) => s.id));
                const newItems = items.filter((i) => !existingIds.has(i.id));
                return [...prev, ...newItems];
            });
            void fetchEquipmentDetails(items);
        },
        [fetchEquipmentDetails]
    );

    const toggleSelection = useCallback(
        (id: string) => {
            const stock = loadedStock.find((item) => item.id === id);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
            setSelectedStocks((prev) => {
                const next = new Map(prev);
                if (next.has(id)) next.delete(id);
                else if (stock) next.set(id, stock);
                return next;
            });
        },
        [loadedStock]
    );

    const allSelected = loadedStock.length > 0 && loadedStock.every((s) => selectedIds.has(s.id));

    const toggleAll = useCallback(() => {
        if (allSelected) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                loadedStock.forEach((stock) => next.delete(stock.id));
                return next;
            });
            setSelectedStocks((prev) => {
                const next = new Map(prev);
                loadedStock.forEach((stock) => next.delete(stock.id));
                return next;
            });
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                loadedStock.forEach((stock) => next.add(stock.id));
                return next;
            });
            setSelectedStocks((prev) => {
                const next = new Map(prev);
                loadedStock.forEach((stock) => next.set(stock.id, stock));
                return next;
            });
        }
    }, [allSelected, loadedStock]);

    const onBack = () => props.onBack();

    const onContinue = useCallback(() => {
        void props.onSubmit(Array.from(selectedStocks.values()));
    }, [selectedStocks, props]);

    return {
        selectedIds,
        selectedCount: selectedStocks.size,
        visibleStockCount: loadedStock.length,
        activeFilter,
        setActiveFilter,
        equipmentById,
        allSelected,
        fetch,
        onDataLoaded,
        toggleSelection,
        toggleAll,
        onBack,
        onContinue,
        isSubmitting: !!props.isSubmitting
    };
};

export default useEquipmentSelectionList;
